import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Download, RefreshCcw, SatelliteDish } from 'lucide-angular';
import { ReaReport, ReaReportSourceItem } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { ReaReportsService } from '../../rea-reports/services/rea-reports.service';
import { WrpcReaReportsService } from '../services/wrpc-rea-reports.service';

@Component({
  standalone: true,
  selector: 'app-wrpc-rea-reports-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './wrpc-rea-reports.page.html',
  styleUrl: './wrpc-rea-reports.page.scss',
})
export class WrpcReaReportsPage implements OnInit {
  private static readonly START_YEAR = 2025;
  private static readonly RPC_REGION = 'WRPC' as const;
  private static readonly MIN_VISIBLE_YEAR = 2026;
  private static readonly MIN_VISIBLE_MONTH = 1;

  private readonly service = inject(ReaReportsService);
  private readonly wrpcService = inject(WrpcReaReportsService);
  private readonly toast = inject(ToastService);

  protected readonly SatelliteDishIcon = SatelliteDish;
  protected readonly DownloadIcon = Download;
  protected readonly RefreshCcwIcon = RefreshCcw;
  protected readonly sourceLoading = signal(false);
  protected readonly saveLoading = signal(false);
  protected readonly autoFetching = signal(false);
  protected readonly sourceReports = signal<ReaReportSourceItem[]>([]);
  protected readonly savedReports = signal<ReaReport[]>([]);
  protected readonly fetchingByKey = signal<Record<string, boolean>>({});
  protected readonly refetchingById = signal<Record<string, boolean>>({});
  protected readonly selectedMonth = signal<number | null>(null);
  protected readonly selectedYear = signal<number | null>(null);

  protected readonly filteredSourceReports = computed(() =>
    this.sourceReports().filter(
      (item) =>
        this.isVisibleSourceReport(item) &&
        (this.selectedMonth() === null || item.report_month === this.selectedMonth()) &&
        (this.selectedYear() === null || item.report_year === this.selectedYear()),
    ),
  );
  protected readonly savedReportsByKey = computed(() =>
    this.savedReports().reduce(
      (lookup, report) => {
        lookup[report.report_key] = report;
        return lookup;
      },
      {} as Record<string, ReaReport>,
    ),
  );
  protected readonly yearOptions = computed(() =>
    Array.from(
      {
        length:
          Math.max(
            new Date().getFullYear(),
            ...this.sourceReports().map((item) => item.report_year ?? WrpcReaReportsPage.START_YEAR),
            this.selectedYear() ?? WrpcReaReportsPage.START_YEAR,
          ) -
          WrpcReaReportsPage.START_YEAR +
          1,
      },
      (_, index) =>
        Math.max(
          new Date().getFullYear(),
          ...this.sourceReports().map((item) => item.report_year ?? WrpcReaReportsPage.START_YEAR),
          this.selectedYear() ?? WrpcReaReportsPage.START_YEAR,
        ) - index,
    ),
  );
  protected readonly monthOptions = computed(() => Array.from({ length: 12 }, (_, index) => index + 1));

  protected readonly latestSourceMonth = computed(() => {
    const first = this.filteredSourceReports()[0] || this.sourceReports()[0];
    if (!first?.report_month || !first?.report_year) {
      return 'Month not detected';
    }
    return this.formatPeriod(first.report_month, first.report_year);
  });

  ngOnInit(): void {
    this.loadSourceReports();
    this.loadSavedReports();
  }

  protected refreshSourceReports(): void {
    this.loadSourceReports();
  }

  protected autoFetchReports(): void {
    this.autoFetching.set(true);
    this.service
      .autoFetch({
        month: this.selectedMonth(),
        year: this.selectedYear(),
        rpcRegion: WrpcReaReportsPage.RPC_REGION,
      })
      .subscribe({
        next: (result) => {
          this.autoFetching.set(false);
          this.toast.show(`Auto fetch complete. Added ${result.created_count}, skipped ${result.skipped_count}.`, '', 'success');
          this.loadSourceReports();
          this.loadSavedReports();
        },
        error: (error) => {
          this.autoFetching.set(false);
          const detail = error?.error?.detail || 'Failed to auto-fetch REA reports';
          this.toast.show(detail, '', 'danger');
        },
      });
  }

  protected fetchReport(report: ReaReportSourceItem): void {
    this.fetchingByKey.set({ ...this.fetchingByKey(), [report.report_key]: true });
    this.wrpcService
      .fetch({
        source_url: report.source_url,
        title: report.title,
        report_month: report.report_month ?? null,
        report_year: report.report_year ?? null,
        report_type: report.report_type,
        rpc_region: report.rpc_region,
      })
      .subscribe({
        next: () => {
          this.fetchingByKey.set({ ...this.fetchingByKey(), [report.report_key]: false });
          this.toast.show('REA report downloaded and saved', '', 'success');
          this.loadSavedReports();
        },
        error: (error) => {
          this.fetchingByKey.set({ ...this.fetchingByKey(), [report.report_key]: false });
          const detail = error?.error?.detail || 'Failed to fetch REA report';
          this.toast.show(detail, '', 'danger');
        },
      });
  }

  protected refetchReport(report: ReaReport): void {
    this.refetchingById.set({ ...this.refetchingById(), [report.id]: true });
    this.wrpcService.refetch(report.id).subscribe({
      next: () => {
        this.refetchingById.set({ ...this.refetchingById(), [report.id]: false });
        this.toast.show('REA report re-downloaded successfully', '', 'success');
        this.loadSavedReports();
      },
      error: () => {
        this.refetchingById.set({ ...this.refetchingById(), [report.id]: false });
        this.toast.show('Failed to re-download REA report', '', 'danger');
      },
    });
  }

  protected downloadReport(report: ReaReport): void {
    window.open(this.service.downloadUrl(report.id), '_blank');
  }

  protected triggerSourceAction(report: ReaReportSourceItem): void {
    const savedReport = this.savedReportsByKey()[report.report_key];
    if (savedReport) {
      this.refetchReport(savedReport);
      return;
    }
    this.fetchReport(report);
  }

  protected sourceActionLabel(report: ReaReportSourceItem): string {
    if (this.isFetchDisabled(report)) {
      return 'View only';
    }
    const savedReport = this.savedReportsByKey()[report.report_key];
    if (savedReport) {
      return this.isRefetching(savedReport.id) ? 'Refetching...' : 'Refetch';
    }
    return this.isFetching(report.report_key) ? 'Fetching...' : 'Fetch';
  }

  protected isSourceActionDisabled(report: ReaReportSourceItem): boolean {
    if (this.isFetchDisabled(report)) {
      return true;
    }
    const savedReport = this.savedReportsByKey()[report.report_key];
    if (savedReport) {
      return this.isRefetching(savedReport.id);
    }
    return this.isFetching(report.report_key);
  }

  protected isFetching(reportKey: string): boolean {
    return !!this.fetchingByKey()[reportKey];
  }

  protected isRefetching(reportId: string): boolean {
    return !!this.refetchingById()[reportId];
  }

  protected updateMonth(value: string): void {
    this.selectedMonth.set(value ? Number(value) : null);
  }

  protected updateYear(value: string): void {
    this.selectedYear.set(value ? Number(value) : null);
  }

  protected monthLabel(month: number): string {
    return new Date(2000, month - 1, 1).toLocaleString(undefined, { month: 'long' });
  }

  protected formatPeriod(month?: number | null, year?: number | null): string {
    if (!month || !year) {
      return 'Unknown period';
    }
    return new Date(year, month - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }

  protected isAfterFetchCutoff(report: ReaReportSourceItem): boolean {
    return this.isFetchDisabled(report);
  }

  protected isVisibleSourceReport(report: ReaReportSourceItem): boolean {
    if (this.isZipReport(report)) {
      return false;
    }
    if (!report.report_year || !report.report_month) {
      return false;
    }
    if (report.report_year > WrpcReaReportsPage.MIN_VISIBLE_YEAR) {
      return true;
    }
    if (report.report_year < WrpcReaReportsPage.MIN_VISIBLE_YEAR) {
      return false;
    }
    return report.report_month >= WrpcReaReportsPage.MIN_VISIBLE_MONTH;
  }

  private isFetchDisabled(report: ReaReportSourceItem): boolean {
    if (this.isZipReport(report)) {
      return true;
    }
    if (this.isRevisedReport(report)) {
      return true;
    }
    if (!report.report_year || !report.report_month) {
      return true;
    }
    if (report.report_year > WrpcReaReportsPage.MIN_VISIBLE_YEAR) {
      return false;
    }
    if (report.report_year < WrpcReaReportsPage.MIN_VISIBLE_YEAR) {
      return true;
    }
    return report.report_month < WrpcReaReportsPage.MIN_VISIBLE_MONTH;
  }

  private isRevisedReport(report: ReaReportSourceItem): boolean {
    return (report.title || '').toLowerCase().includes('revised');
  }

  private isZipReport(report: ReaReportSourceItem): boolean {
    return (report.file_name || '').toLowerCase().endsWith('.zip') || (report.title || '').toLowerCase().includes('zip');
  }

  private loadSourceReports(): void {
    this.sourceLoading.set(true);
    this.service.listSource(WrpcReaReportsPage.RPC_REGION).subscribe({
      next: (rows) => {
        this.sourceReports.set(rows || []);
        this.sourceLoading.set(false);
      },
      error: (error) => {
        this.sourceReports.set([]);
        this.sourceLoading.set(false);
        const detail = error?.error?.detail || 'Failed to load WRPC source reports';
        this.toast.show(detail, '', 'danger');
      },
    });
  }

  private loadSavedReports(): void {
    this.saveLoading.set(true);
    this.service.listSaved(WrpcReaReportsPage.RPC_REGION).subscribe({
      next: (rows) => {
        this.savedReports.set(rows || []);
        this.saveLoading.set(false);
      },
      error: () => {
        this.savedReports.set([]);
        this.saveLoading.set(false);
        this.toast.show('Failed to load saved REA reports', '', 'danger');
      },
    });
  }
}
